import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Drawer,
  DrawerContent,
  DrawerClose,
} from "@/components/ui/drawer";
import ReminderPausePanel from "@/components/ReminderPausePanel";

const DELETION_REASONS = [
  "I don't use it enough",
  "I found something better",
  "Missing a feature I need",
  "Too many emails or notifications",
  "Privacy concerns",
  "I created a duplicate account",
  "Other",
] as const;

type DeletionReason = (typeof DELETION_REASONS)[number];

interface Intervention {
  icon: string;
  title: string;
  body: string;
  actionLabel?: string;
  actionRoute?: string;
  actionExternal?: boolean;
  showPauseSelector?: boolean;
}

const INTERVENTIONS: Partial<Record<DeletionReason, Intervention>> = {
  "I don't use it enough": {
    icon: "💛",
    title: "You can pause without leaving.",
    body: "Turn off reminders and come back whenever you're ready. Your Circle and relationship history will be here waiting.",
    showPauseSelector: true,
  },
  "Missing a feature I need": {
    icon: "🛠️",
    title: "We're building fast.",
    body: "Relationship timelines, expanded occasions, and more are coming in the next 60 days. Your feedback today shapes what gets built next.",
    actionLabel: "See what's coming",
    actionRoute: "https://circle.dearestly.co",
    actionExternal: true,
  },
  "Too many emails or notifications": {
    icon: "🔕",
    title: "You can reduce emails without leaving.",
    body: "Update your notification preferences in Settings to reduce how often we reach out. You stay in control.",
    actionLabel: "Update preferences instead",
    actionRoute: "/settings/preferences",
  },
  "I found something better": {
    icon: "🙏",
    title: "Thank you for trying Circle.",
    body: "We're genuinely sorry to see you go. Every piece of feedback helps us build something better for the people who come after you.",
  },
  "Privacy concerns": {
    icon: "🙏",
    title: "Thank you for trying Circle.",
    body: "We're genuinely sorry to see you go. Every piece of feedback helps us build something better for the people who come after you.",
  },
  Other: {
    icon: "🙏",
    title: "Thank you for trying Circle.",
    body: "We're genuinely sorry to see you go. Every piece of feedback helps us build something better for the people who come after you.",
  },
};

interface UserStats {
  contactCount: number;
  messagesGenerated: number;
  reflectionLikes: number;
  accountAgeMonths: number;
}

interface DeleteAccountFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stats: UserStats;
}

const DeleteAccountFlow = ({ open, onOpenChange, stats }: DeleteAccountFlowProps) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [reason, setReason] = useState<DeletionReason | null>(null);
  const [feedback, setFeedback] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const resetFlow = () => {
    setStep(1);
    setReason(null);
    setFeedback("");
    setDeleting(false);
    setPauseLoading(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(resetFlow, 300);
  };

  const handleContinueFromSurvey = () => {
    if (!reason) return;
    if (reason === "I created a duplicate account") {
      setStep(3);
    } else {
      setStep(2);
    }
  };

  const handleInterventionAction = (intervention: Intervention) => {
    if (intervention.actionExternal) {
      window.open(intervention.actionRoute, "_blank");
    } else if (intervention.actionRoute) {
      navigate(intervention.actionRoute);
    }
    handleClose();
  };

  const handlePauseFromDeletion = async (days: number) => {
    if (!user) return;
    setPauseLoading(true);
    const pauseDate = new Date();
    pauseDate.setDate(pauseDate.getDate() + days);
    const { error } = await supabase
      .from("profiles")
      .update({
        reminders_paused_until: pauseDate.toISOString(),
        reminders_paused_at: new Date().toISOString(),
        email_subscribed: true,
      })
      .eq("user_id", user.id);
    setPauseLoading(false);
    if (!error) {
      handleClose();
      toast.success("Reminders paused 💛 — your Circle is safe.");
    } else {
      toast.error("Failed to pause. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!reason) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: {
          deletion_reason: reason,
          deletion_feedback: feedback || null,
        },
      });
      if (error) throw error;

      const firstName = data?.first_name || null;
      await signOut();
      navigate("/goodbye", { state: { firstName } });
    } catch (error) {
      console.error("Delete account error:", error);
      toast.error("Failed to delete account. Please try again.");
      setDeleting(false);
    }
  };

  const hasStats =
    stats.contactCount > 0 ||
    stats.messagesGenerated > 0 ||
    stats.reflectionLikes > 0 ||
    stats.accountAgeMonths > 0;

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(true); }}>
      <DrawerContent className="max-h-[85vh]">
        {/* Step 1: Exit Survey */}
        {step === 1 && (
          <div className="px-6 pb-8 pt-2 overflow-y-auto max-h-[calc(85vh-2rem)]">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-serif text-xl font-bold text-foreground">Before you go</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Help us understand so we can do better.
                </p>
              </div>
              <DrawerClose asChild>
                <button className="p-1 rounded-full hover:bg-muted transition-colors" aria-label="Close">
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </DrawerClose>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">Why are you leaving Circle?</p>
              <div className="flex flex-col gap-2">
                {DELETION_REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setReason(r)}
                    className={`w-full text-left px-4 py-3 rounded-full border text-sm transition-colors ${
                      reason === r
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:border-foreground/30"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <p className="text-sm font-semibold text-foreground">
                Anything we could have done differently?
              </p>
              <div className="relative">
                <Textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value.slice(0, 300))}
                  placeholder="Optional — but we read every response 💛"
                  rows={3}
                  className="rounded-2xl resize-none focus-visible:ring-ring"
                  maxLength={300}
                />
                <span className="absolute bottom-2 right-3 text-xs text-muted-foreground">
                  {feedback.length}/300
                </span>
              </div>
            </div>

            <Button
              onClick={handleContinueFromSurvey}
              disabled={!reason}
              className="w-full mt-6 rounded-full"
              size="lg"
            >
              Continue
            </Button>

            <p className="text-xs text-center mt-4" style={{ color: "hsl(0 0% 60%)" }}>
              Your feedback is stored privately and used only to improve Circle.
            </p>
          </div>
        )}

        {/* Step 2: Soft Intervention */}
        {step === 2 && reason && INTERVENTIONS[reason] && (
          <div className="px-6 pb-8 pt-2 overflow-y-auto max-h-[calc(85vh-2rem)]">
            <div className="rounded-2xl bg-card p-5 shadow-sm border-l-[3px] border-l-accent space-y-3">
              <div className="text-center text-3xl">{INTERVENTIONS[reason]!.icon}</div>
              <h3 className="font-serif font-bold text-base text-foreground text-center">
                {INTERVENTIONS[reason]!.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed text-center">
                {INTERVENTIONS[reason]!.body}
              </p>

              {/* Inline pause selector for "I don't use it enough" */}
              {INTERVENTIONS[reason]!.showPauseSelector && (
                <div className="mt-3">
                  <ReminderPausePanel
                    compact
                    loading={pauseLoading}
                    onPause={handlePauseFromDeletion}
                    onTurnOff={() => setStep(3)}
                    onCancel={handleClose}
                  />
                </div>
              )}

              {/* Standard action button for other interventions */}
              {!INTERVENTIONS[reason]!.showPauseSelector && INTERVENTIONS[reason]!.actionLabel && (
                <Button
                  variant="outline"
                  className="w-full rounded-full mt-2"
                  onClick={() => handleInterventionAction(INTERVENTIONS[reason]!)}
                >
                  {INTERVENTIONS[reason]!.actionLabel}
                </Button>
              )}
            </div>

            {/* Only show continue/cancel for non-pause interventions */}
            {!INTERVENTIONS[reason]!.showPauseSelector && (
              <div className="mt-6 space-y-3">
                <Button
                  onClick={() => setStep(3)}
                  className="w-full rounded-full"
                  size="lg"
                >
                  Continue with deletion
                </Button>
                <button
                  onClick={handleClose}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Actually, keep my account
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="px-6 pb-8 pt-2 overflow-y-auto max-h-[calc(85vh-2rem)]">
            <div className="mb-4">
              <h2 className="font-serif text-xl font-bold text-foreground">Delete your account</h2>
              <p className="text-sm text-muted-foreground mt-1">This cannot be undone.</p>
            </div>

            {hasStats && (
              <div className="rounded-2xl bg-card p-4 shadow-sm border-l-[3px] space-y-2" style={{ borderLeftColor: "hsl(0 84% 50%)" }}>
                {stats.contactCount > 0 && (
                  <p className="text-sm">
                    🫂 <span className="font-bold">{stats.contactCount}</span>{" "}
                    <span className="text-muted-foreground">people in your Circle</span>
                  </p>
                )}
                {stats.messagesGenerated > 0 && (
                  <p className="text-sm">
                    💌 <span className="font-bold">{stats.messagesGenerated}</span>{" "}
                    <span className="text-muted-foreground">messages written</span>
                  </p>
                )}
                {stats.reflectionLikes > 0 && (
                  <p className="text-sm">
                    💛 <span className="font-bold">{stats.reflectionLikes}</span>{" "}
                    <span className="text-muted-foreground">Daily Reflections liked</span>
                  </p>
                )}
                {stats.accountAgeMonths > 0 && (
                  <p className="text-sm">
                    📅 <span className="font-bold">{stats.accountAgeMonths}</span>{" "}
                    <span className="text-muted-foreground">months of relationship history</span>
                  </p>
                )}
                <p className="text-sm text-muted-foreground italic mt-2">
                  All of this will be permanently deleted.
                </p>
              </div>
            )}

            <div className="mt-6 space-y-3">
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="w-full rounded-full"
                size="lg"
                variant="destructive"
              >
                {deleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  "Yes, delete my account"
                )}
              </Button>
              <button
                onClick={handleClose}
                disabled={deleting}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default DeleteAccountFlow;
